import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Event as EventIcon,
  Message as MessageIcon,
  SmartToy as SmartToyIcon,
  Analytics as AnalyticsIcon,
  AdminPanelSettings as AdminIcon,
  Settings as SettingsIcon,
  Restaurant as RestaurantIcon,
  CardGiftcard as CardGiftcardIcon,
  Add as AddIcon,
  Business as BusinessIcon,
  AccountCircle as AccountCircleIcon,
} from '@mui/icons-material';
import { Logo } from './Logo';
import { Vendor } from '../types';
import CreateBusinessDialog from './CreateBusinessDialog';
import { useBusiness } from '../contexts/BusinessContext';

const drawerWidth = 240;

interface LayoutProps {
  children: React.ReactNode;
  user: any;
  signOut?: () => void;
  isAdminRoute?: boolean;
}

const vendorMenuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Queue', icon: <RestaurantIcon />, path: '/queue' },
  { text: 'Customers', icon: <PeopleIcon />, path: '/customers' },
  { text: 'Appointments', icon: <EventIcon />, path: '/appointments' },
  { text: 'Messages', icon: <MessageIcon />, path: '/messages' },
  { text: 'AI Agent', icon: <SmartToyIcon />, path: '/agent-config' },
  { text: 'Analytics', icon: <AnalyticsIcon />, path: '/analytics' },
  { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
  { text: 'Menu', icon: <RestaurantIcon />, path: '/menu' },
  { text: 'Rewards', icon: <CardGiftcardIcon />, path: '/rewards' },
];

const adminMenuItems = [
  { text: 'Mission Control', icon: <AdminIcon />, path: '/admin' },
  // Note: Vendors, System Health, AI Usage, and Growth are tabs within Mission Control
];

export default function Layout({ children, user, signOut, isAdminRoute = false }: LayoutProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Use global business context
  const { 
    vendors, 
    selectedVendorId, 
    switchBusiness, 
    loading: loadingVendors,
    refreshVendors 
  } = useBusiness();
  
  // Get selected vendor to check business type
  const selectedVendor = vendors.find(v => v.vendorId === selectedVendorId);
  const businessType = selectedVendor?.businessType || 'order';
  
  // Filter menu items based on business type
  const getFilteredMenuItems = () => {
    if (isAdminRoute) {
      return adminMenuItems;
    }
    
    // If business type is 'order' only, exclude Appointments
    if (businessType === 'order') {
      return vendorMenuItems.filter(item => item.text !== 'Appointments');
    }
    
    // For 'appointment' or 'hybrid', show all items including Appointments
    return vendorMenuItems;
  };
  
  const menuItems = getFilteredMenuItems();

  const handleCreateBusinessSuccess = (newVendor: Vendor) => {
    // Refresh vendor list via context
    refreshVendors().then(() => {
      // Switch to new business
      switchBusiness(newVendor.vendorId);
      // Close dialog
      setCreateDialogOpen(false);
    });
  };

  const handleVendorChange = (vendorId: string) => {
    console.log('[LAYOUT-CHANGE] Business selected:', vendorId);
    if (vendorId === 'create') {
      setCreateDialogOpen(true);
      return;
    }

    // Use context to switch business
    switchBusiness(vendorId);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <div>
      <Toolbar sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <Logo size="small" />
      </Toolbar>
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              component={Link}
              to={item.path}
              selected={location.pathname === item.path}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar sx={{ gap: 2 }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
            <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
              <Logo size="small" showText={false} />
            </Box>
            <Typography variant="h6" noWrap component="div" sx={{ ml: { sm: 1 } }}>
              {isAdminRoute ? 'Mission Control' : 'Vendor Dashboard'}
            </Typography>
          </Box>

          {/* Business Switcher - Only show for vendor dashboard, not Mission Control */}
          {!isAdminRoute && !loadingVendors && (
            vendors.length > 0 ? (
              <FormControl 
                size="small" 
                sx={{ 
                  minWidth: 240,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    },
                  },
                  '& .MuiOutlinedInput-notchedOutline': { 
                    borderColor: 'rgba(255,255,255,0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': { 
                    borderColor: 'rgba(255,255,255,0.5)',
                  },
                  '& .MuiSvgIcon-root': { color: 'white' },
                }}
              >
                <Select
                  value={selectedVendorId || vendors[0]?.vendorId || ''}
                  onChange={(e) => handleVendorChange(String(e.target.value))}
                  displayEmpty
                  renderValue={(value) => {
                    if (!value || value === 'create') {
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BusinessIcon sx={{ fontSize: 18 }} />
                          <Typography>Select Business</Typography>
                        </Box>
                      );
                    }
                    const vendor = vendors.find(v => v.vendorId === value);
                    return vendor ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BusinessIcon sx={{ fontSize: 18 }} />
                        <Typography>{vendor.businessName}</Typography>
                      </Box>
                    ) : value;
                  }}
                  sx={{
                    color: 'white',
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                      py: 1,
                    },
                  }}
                >
                  {vendors.map((v) => (
                    <MenuItem key={v.vendorId} value={v.vendorId}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <BusinessIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body2">{v.businessName}</Typography>
                          {v.industry && (
                            <Typography variant="caption" color="text.secondary">
                              {v.industry}
                            </Typography>
                          )}
                        </Box>
                        {v.subscriptionTier && v.subscriptionTier !== 'free' && (
                          <Chip 
                            label={v.subscriptionTier} 
                            size="small" 
                            color="primary"
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                  <Divider sx={{ my: 0.5 }} />
                  <MenuItem value="create" sx={{ color: 'primary.main', fontWeight: 500 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AddIcon fontSize="small" />
                      Create New Business
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            ) : (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogOpen(true)}
                sx={{
                  color: 'white',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                Create Business
              </Button>
            )
          )}

          {/* User Info */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(255,255,255,0.2)' }}>
              <AccountCircleIcon />
            </Avatar>
            <Typography 
              variant="body2" 
              sx={{ 
                display: { xs: 'none', md: 'block' },
                maxWidth: 150,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {user?.signInDetails?.loginId || user?.username || user?.userId || 'User'}
            </Typography>
          </Box>

          {signOut && (
            <Button 
              color="inherit" 
              onClick={signOut}
              sx={{ 
                ml: 1,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Sign Out
            </Button>
          )}
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        {children}
      </Box>

      <CreateBusinessDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={handleCreateBusinessSuccess}
      />
    </Box>
  );
}

